import { useState } from 'react'
import {
  useClubAdminCreateCheckoutSessionMutation,
  useClubAdminCreatePortalSessionMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { getApiErrorCode } from '../../utils/ApiError.ts'

export function useBillingRedirect(clubId: string) {
  const [createCheckoutSession, { isLoading: checkoutLoading }] =
    useClubAdminCreateCheckoutSessionMutation()
  const [createPortalSession, { isLoading: portalLoading }] =
    useClubAdminCreatePortalSessionMutation()
  const [redirecting, setRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // `redirecting` keeps the button disabled after the URL arrives, until the
  // browser actually leaves the page.
  const redirect = (url: string) => {
    setRedirecting(true)
    window.location.assign(url)
  }

  const openCheckout = async () => {
    const { url } = await createCheckoutSession(clubId).unwrap()
    redirect(url)
  }

  const openPortal = async () => {
    const { url } = await createPortalSession(clubId).unwrap()
    redirect(url)
  }

  // The 422 fallbacks are deliberately one level deep, never recursive:
  // SUBSCRIPTION_ALREADY_ACTIVE means the club already pays (→ portal),
  // BILLING_ACCOUNT_NOT_FOUND means it never had a card (→ checkout).
  const subscribe = async () => {
    setError(null)
    try {
      await openCheckout()
    } catch (checkoutError) {
      if (getApiErrorCode(checkoutError) === 'SUBSCRIPTION_ALREADY_ACTIVE') {
        try {
          await openPortal()
          return
        } catch {
          /* fall through */
        }
      }
      setError('Could not start checkout. Please try again.')
    }
  }

  const manageSubscription = async () => {
    setError(null)
    try {
      await openPortal()
    } catch (portalError) {
      if (getApiErrorCode(portalError) === 'BILLING_ACCOUNT_NOT_FOUND') {
        try {
          await openCheckout()
          return
        } catch {
          /* fall through */
        }
      }
      setError('Could not open the billing portal. Please try again.')
    }
  }

  return {
    subscribe,
    manageSubscription,
    isBusy: checkoutLoading || portalLoading || redirecting,
    error,
  }
}
